import { useState, useEffect } from "react";
import { api } from "../../App";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  X, Settings, Mail, Globe, Bell, Trash2, Plus, Server, User, Send, Edit
} from "lucide-react";

export default function SettingsModal({ onClose }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [blocklist, setBlocklist] = useState([]);
  const [blockForm, setBlockForm] = useState({ email: "", name: "" });
  const [blocklistFile, setBlocklistFile] = useState(null);
  const [blocklistPreview, setBlocklistPreview] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testConfigId, setTestConfigId] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [settings, setSettings] = useState({
    default_sending_limit: 100,
    timezone: "UTC",
    branding_name: "Vessel Email Marketing",
    emails_per_batch: 50,
    batch_pause_minutes: 5
  });

  const [newConfig, setNewConfig] = useState({
    name: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    from_name: "",
    from_email: "",
    is_default: false
  });

  const [showAddConfig, setShowAddConfig] = useState(false);
  const [editConfig, setEditConfig] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configsRes, settingsRes, blockRes] = await Promise.all([
        api.get("/email-configs"),
        api.get("/settings"),
        api.get("/blocklist")
      ]);
      setEmailConfigs(configsRes.data);
      setSettings(settingsRes.data);
      setBlocklist(blockRes.data);
      
      // Set default test config
      const defaultConfig = configsRes.data.find(c => c.is_default);
      if (defaultConfig) {
        setTestConfigId(defaultConfig.config_id);
      } else if (configsRes.data.length > 0) {
        setTestConfigId(configsRes.data[0].config_id);
      }
    } catch (error) {
      console.error("Failed to load settings");
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmailConfig = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post("/email-configs", newConfig);
      toast.success("Email configuration added");
      setNewConfig({
        name: "",
        smtp_host: "",
        smtp_port: 587,
        smtp_username: "",
        smtp_password: "",
        from_name: "",
        from_email: "",
        is_default: false
      });
      setShowAddConfig(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    try {
      await api.delete(`/email-configs/${configId}`);
      toast.success("Configuration deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete configuration");
    }
  };

  const handleEditConfig = (config) => {
    setEditConfig({
      ...config,
      smtp_password: ""
    });
    setShowAddConfig(false);
  };

  const handleUpdateEmailConfig = async (e) => {
    e.preventDefault();
    if (!editConfig) return;
    
    setLoading(true);
    try {
      const payload = {
        name: editConfig.name,
        smtp_host: editConfig.smtp_host,
        smtp_port: editConfig.smtp_port,
        smtp_username: editConfig.smtp_username,
        smtp_password: editConfig.smtp_password,
        from_name: editConfig.from_name,
        from_email: editConfig.from_email,
        is_default: editConfig.is_default
      };
      await api.put(`/email-configs/${editConfig.config_id}`, payload);
      toast.success("Email configuration updated");
      setEditConfig(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyConfig = async (configId) => {
    try {
      const response = await api.post(`/email-configs/${configId}/verify`);
      toast.success(response.data.message || "SMTP connection verified");
    } catch (error) {
      toast.error(error.response?.data?.detail || "SMTP verification failed");
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    if (!testConfigId) {
      toast.error("Please select an email configuration to send from");
      return;
    }
    
    setSendingTest(true);
    try {
      const response = await api.post("/email-configs/test", { 
        to_email: testEmail,
        config_id: testConfigId
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();
    if (!blockForm.email) {
      toast.error("Email is required");
      return;
    }
    try {
      const response = await api.post("/blocklist", blockForm);
      setBlocklist(prev => [response.data, ...prev]);
      setBlockForm({ email: "", name: "" });
      toast.success("Added to blocklist");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to blocklist");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await api.delete(`/blocklist/${blockId}`);
      setBlocklist(prev => prev.filter(b => b.block_id !== blockId));
      toast.success("Removed from blocklist");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove from blocklist");
    }
  };

  const handleBlocklistPreview = async () => {
    if (!blocklistFile) {
      toast.error("Select a CSV/Excel file first");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", blocklistFile);
      const response = await api.post("/blocklist/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBlocklistPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview blocklist import");
    }
  };

  const handleBlocklistImport = async () => {
    if (!blocklistFile) {
      toast.error("Select a CSV/Excel file first");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", blocklistFile);
      const response = await api.post("/blocklist/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Imported ${response.data.imported} emails (${response.data.skipped} skipped)`);
      setBlocklistFile(null);
      setBlocklistPreview(null);
      const list = await api.get("/blocklist");
      setBlocklist(list.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import blocklist");
    }
  };

  const handleBlocklistExport = async () => {
    try {
      const response = await api.get("/blocklist/export/csv", { responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "blocklist-export.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Blocklist exported");
    } catch (error) {
      toast.error("Failed to export blocklist");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fadeIn">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Settings className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Settings
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-8rem)]">
          <Tabs defaultValue="email" className="w-full">
            <div className="border-b border-border px-6">
              <TabsList className="h-auto p-0 bg-transparent">
                <TabsTrigger 
                  value="email" 
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email Config
                </TabsTrigger>
                <TabsTrigger 
                  value="general"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  General
                </TabsTrigger>
                <TabsTrigger 
                  value="account"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                >
                  <User className="h-4 w-4 mr-2" />
                  Account
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Email Configuration Tab */}
            <TabsContent value="email" className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Email Configurations</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage SMTP settings for sending emails
                  </p>
                </div>
                <Button 
                  onClick={() => setShowAddConfig(true)}
                  className="btn-primary"
                  data-testid="add-email-config-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Configuration
                </Button>
              </div>

              {/* SMTP Help Info */}
              <Card className="border-amber-500/30 bg-amber-500/5">
                <CardContent className="p-4">
                  <h4 className="font-medium text-amber-400 mb-2">Common SMTP Settings</h4>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p><strong>Gmail:</strong> smtp.gmail.com, Port 587, Use App Password (not regular password)</p>
                    <p><strong>SendGrid:</strong> smtp.sendgrid.net, Port 587, Username: apikey, Password: Your API Key</p>
                    <p><strong>Outlook/Office365:</strong> smtp.office365.com, Port 587</p>
                    <p><strong>Mailgun:</strong> smtp.mailgun.org, Port 587</p>
                    <p className="text-xs mt-2 text-amber-400">Note: For Gmail, you must enable 2FA and create an App Password at myaccount.google.com → Security → App Passwords</p>
                  </div>
                </CardContent>
              </Card>

              {/* Add Config Form */}
              {showAddConfig && (
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-base">New Email Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAddEmailConfig} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Configuration Name *</Label>
                          <Input
                            value={newConfig.name}
                            onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                            required
                            placeholder="Production SMTP"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>SMTP Host *</Label>
                          <Input
                            value={newConfig.smtp_host}
                            onChange={(e) => setNewConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                            required
                            placeholder="smtp.sendgrid.net"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>SMTP Port *</Label>
                          <Input
                            type="number"
                            value={newConfig.smtp_port}
                            onChange={(e) => setNewConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Username *</Label>
                          <Input
                            value={newConfig.smtp_username}
                            onChange={(e) => setNewConfig(prev => ({ ...prev, smtp_username: e.target.value }))}
                            required
                            placeholder="apikey"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Password *</Label>
                          <Input
                            type="password"
                            value={newConfig.smtp_password}
                            onChange={(e) => setNewConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>From Name *</Label>
                          <Input
                            value={newConfig.from_name}
                            onChange={(e) => setNewConfig(prev => ({ ...prev, from_name: e.target.value }))}
                            required
                            placeholder="Maritime Supplies"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>From Email *</Label>
                          <Input
                            type="email"
                            value={newConfig.from_email}
                            onChange={(e) => setNewConfig(prev => ({ ...prev, from_email: e.target.value }))}
                            required
                            placeholder="noreply@company.com"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="is_default"
                          checked={newConfig.is_default}
                          onChange={(e) => setNewConfig(prev => ({ ...prev, is_default: e.target.checked }))}
                          className="rounded"
                        />
                        <Label htmlFor="is_default" className="cursor-pointer">
                          Set as default configuration
                        </Label>
                      </div>

                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setShowAddConfig(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={loading}>
                          {loading ? "Adding..." : "Add Configuration"}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {editConfig && (
                <Card className="border-blue-500/30">
                  <CardHeader>
                    <CardTitle className="text-base">Edit Email Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleUpdateEmailConfig} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Configuration Name *</Label>
                          <Input
                            value={editConfig.name}
                            onChange={(e) => setEditConfig(prev => ({ ...prev, name: e.target.value }))}
                            required
                            placeholder="Production SMTP"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>SMTP Host *</Label>
                          <Input
                            value={editConfig.smtp_host}
                            onChange={(e) => setEditConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                            required
                            placeholder="smtp.sendgrid.net"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>SMTP Port *</Label>
                          <Input
                            type="number"
                            value={editConfig.smtp_port}
                            onChange={(e) => setEditConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Username *</Label>
                          <Input
                            value={editConfig.smtp_username}
                            onChange={(e) => setEditConfig(prev => ({ ...prev, smtp_username: e.target.value }))}
                            required
                            placeholder="apikey"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Password (leave blank to keep)</Label>
                          <Input
                            type="password"
                            value={editConfig.smtp_password}
                            onChange={(e) => setEditConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                            placeholder="••••••••"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>From Name *</Label>
                          <Input
                            value={editConfig.from_name}
                            onChange={(e) => setEditConfig(prev => ({ ...prev, from_name: e.target.value }))}
                            required
                            placeholder="Maritime Supplies"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>From Email *</Label>
                          <Input
                            type="email"
                            value={editConfig.from_email}
                            onChange={(e) => setEditConfig(prev => ({ ...prev, from_email: e.target.value }))}
                            required
                            placeholder="noreply@company.com"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="edit-is_default"
                          checked={editConfig.is_default}
                          onChange={(e) => setEditConfig(prev => ({ ...prev, is_default: e.target.checked }))}
                          className="rounded"
                        />
                        <Label htmlFor="edit-is_default" className="cursor-pointer">
                          Set as default configuration
                        </Label>
                      </div>

                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setEditConfig(null)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={loading}>
                          {loading ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Test Email Section */}
              <Card className="border-border bg-secondary/20">
                <CardContent className="p-4">
                  <h4 className="font-medium text-foreground mb-3">Test Email Configuration</h4>
                  <div className="space-y-3">
                    {/* Send From Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm text-foreground">Send From</Label>
                      <select
                        value={testConfigId}
                        onChange={(e) => setTestConfigId(e.target.value)}
                        className="w-full px-3 py-2 bg-secondary border border-border rounded-md text-foreground"
                      >
                        <option value="">Select email configuration</option>
                        {emailConfigs.map((config) => (
                          <option key={config.config_id} value={config.config_id}>
                            {config.from_email} {config.is_default ? "(Default)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Send To */}
                    <div className="flex gap-3">
                      <Input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="Enter email to receive test"
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleSendTestEmail}
                        disabled={sendingTest || !testEmail || !testConfigId}
                        className="btn-primary"
                      >
                        {sendingTest ? (
                          "Sending..."
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send Test
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Send a test email to verify your configuration is working correctly
                  </p>
                </CardContent>
              </Card>

              {/* Existing Configs */}
              <div className="space-y-3">
                {emailConfigs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Server className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p>No email configurations yet</p>
                  </div>
                ) : (
                  emailConfigs.map((config) => (
                    <Card key={config.config_id} className="border-border">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded">
                              <Mail className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{config.name}</span>
                                {config.is_default && (
                                  <Badge className="badge-scheduled">Default</Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {config.smtp_host}:{config.smtp_port} • {config.from_email}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditConfig(config)}
                              className="text-blue-500 hover:text-blue-600"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleVerifyConfig(config.config_id)}
                              className="text-emerald-500 hover:text-emerald-600"
                              title="Verify SMTP"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteConfig(config.config_id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Blocklist */}
              <div className="space-y-3">
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-base">Global Blocklist</CardTitle>
                    <CardDescription>
                      Emails here will be skipped even if included in imports or campaigns.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setBlocklistFile(e.target.files?.[0] || null)}
                        className="max-w-xs"
                      />
                      <Button type="button" variant="outline" onClick={handleBlocklistPreview}>
                        Preview CSV
                      </Button>
                      <Button type="button" variant="outline" onClick={handleBlocklistImport}>
                        Import CSV
                      </Button>
                      <Button type="button" variant="outline" onClick={handleBlocklistExport}>
                        Export CSV
                      </Button>
                    </div>

                    {blocklistPreview && (
                      <div className="border border-border rounded-lg overflow-auto max-h-56 mb-4">
                        <div className="p-2 text-xs text-muted-foreground">
                          Preview (first 20 rows) — Detected {blocklistPreview.total_detected} emails
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-secondary/50">
                            <tr>
                              {blocklistPreview.headers.map((h) => (
                                <th key={h} className="px-3 py-2 text-left text-muted-foreground">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {blocklistPreview.rows.map((row, idx) => (
                              <tr key={idx} className="hover:bg-secondary/30">
                                {blocklistPreview.headers.map((h) => (
                                  <td key={`${idx}-${h}`} className="px-3 py-2 text-muted-foreground">
                                    {String(row[h] ?? "")}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <form onSubmit={handleAddBlock} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        type="email"
                        placeholder="Email (required)"
                        value={blockForm.email}
                        onChange={(e) => setBlockForm(prev => ({ ...prev, email: e.target.value }))}
                      />
                      <Input
                        placeholder="Name (optional)"
                        value={blockForm.name}
                        onChange={(e) => setBlockForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                      <Button type="submit" className="btn-primary">
                        Add
                      </Button>
                    </form>

                    <div className="mt-4 space-y-2">
                      {blocklist.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No blocked emails</p>
                      ) : (
                        blocklist.map((entry) => (
                          <div key={entry.block_id} className="flex items-center justify-between p-2 border border-border rounded">
                            <div>
                              <p className="text-sm font-medium">{entry.email}</p>
                              {entry.name && <p className="text-xs text-muted-foreground">{entry.name}</p>}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteBlock(entry.block_id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* General Settings Tab */}
            <TabsContent value="general" className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">General Settings</h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Default Sending Limit (per day)</Label>
                    <Input
                      type="number"
                      value={settings.default_sending_limit}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        default_sending_limit: parseInt(e.target.value) 
                      }))}
                      min={1}
                      max={10000}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Timezone</Label>
                    <Input
                      value={settings.timezone}
                      onChange={(e) => setSettings(prev => ({ ...prev, timezone: e.target.value }))}
                      placeholder="UTC"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Branding Name</Label>
                    <Input
                      value={settings.branding_name}
                      onChange={(e) => setSettings(prev => ({ ...prev, branding_name: e.target.value }))}
                      placeholder="Vessel Email Marketing"
                    />
                  </div>
                </div>
              </div>

              {/* Rate Limiting Settings */}
              <div className="border-t border-border pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">Email Rate Limiting</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Control how many emails are sent before pausing. This helps prevent being marked as spam.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Emails per Batch</Label>
                    <Input
                      type="number"
                      value={settings.emails_per_batch}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        emails_per_batch: parseInt(e.target.value) || 50
                      }))}
                      min={1}
                      max={500}
                    />
                    <p className="text-xs text-muted-foreground">
                      Number of emails to send before pausing (default: 50)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground">Pause Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={settings.batch_pause_minutes}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        batch_pause_minutes: parseInt(e.target.value) || 5
                      }))}
                      min={1}
                      max={60}
                    />
                    <p className="text-xs text-muted-foreground">
                      Minutes to wait between batches (default: 5)
                    </p>
                  </div>
                </div>

                <Card className="mt-4 border-blue-500/30 bg-blue-500/5">
                  <CardContent className="p-4">
                    <p className="text-sm text-blue-400">
                      <strong>Example:</strong> With {settings.emails_per_batch || 50} emails per batch and {settings.batch_pause_minutes || 5} minute pause, 
                      sending 200 emails will take approximately {Math.ceil((200 / (settings.emails_per_batch || 50)) - 1) * (settings.batch_pause_minutes || 5)} minutes.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Button onClick={handleSaveSettings} className="btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Save Settings"}
              </Button>
            </TabsContent>

            {/* Account Tab */}
            <TabsContent value="account" className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Account Information</h3>
                
                <Card className="border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-6">
                      {user?.picture ? (
                        <img 
                          src={user.picture} 
                          alt={user.name}
                          className="h-16 w-16 rounded-full"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-8 w-8 text-primary" />
                        </div>
                      )}
                      <div>
                        <h4 className="text-xl font-semibold">{user?.name}</h4>
                        <p className="text-muted-foreground">{user?.email}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-border">
                        <span className="text-muted-foreground">User ID</span>
                        <span className="font-mono text-sm">{user?.user_id}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border">
                        <span className="text-muted-foreground">Account Type</span>
                        <span>Standard</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="flex justify-end p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
